import { type ColumnDef } from "components/table";
import { createAppColumnHelper } from "shared/react-table/appTable";
import type { TFunction } from "i18next";
import { ChevronRight } from "lucide-react";
import { Link, type HistoryState } from "@tanstack/react-router";
import { Checkbox } from "components/ui/checkbox";
import { TruncatedText } from "components/TruncatedText";
import { Text } from "components/text";
import { DataTableColumnHeader } from "components/ui/data-table-column-header";
import { type SearchProductWithScore } from "hooks/search/types";
import { deriveMatchSources } from "hooks/search/matchSources";
import { MatchTypeBadges } from "./MatchTypeBadges";

export const MATCH_TYPE_COLUMN_ID = "matchType";

const columnHelper = createAppColumnHelper<SearchProductWithScore>();

interface HybridColumnsOptions {
  loading: boolean;
  linkState?: HistoryState;
}

export const hybridColumns = (
  t: TFunction,
  options: HybridColumnsOptions,
): ColumnDef<SearchProductWithScore>[] =>
  columnHelper.columns([
    columnHelper.display({
      id: "select",
      enableHiding: false,
      size: 50,
      header: ({ table }) =>
        options.loading ? null : (
          <div className="flex justify-center">
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value: boolean) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          </div>
        ),
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Select row"
          />
        </div>
      ),
    }),
    columnHelper.display({
      id: "expand",
      enableHiding: false,
      size: 50,
      header: () => null,
      cell: ({ row }) => {
        const isExpanded = row.getIsExpanded();
        return (
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={`detail-row-${row.original.id}`}
            aria-label={
              isExpanded
                ? t("books:booktable.collapseLabel", {
                    title: row.original.title,
                  })
                : t("books:booktable.expandLabel", {
                    title: row.original.title,
                  })
            }
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            className={`flex h-6 w-6 items-center justify-center rounded transition-colors ${
              isExpanded
                ? "bg-primary-50 text-primary-500"
                : "text-secondary-500 group-hover:bg-primary-50 group-hover:text-primary-500"
            }`}
          >
            <ChevronRight
              size={16}
              className={`transition-transform duration-200 ${
                isExpanded ? "rotate-90" : ""
              }`}
            />
          </button>
        );
      },
    }),
    columnHelper.accessor("isbn", {
      enableHiding: false,
      size: 150,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("books:booktable.columns.isbn")}
        />
      ),
      cell: ({ row }) => <TruncatedText text={row.getValue("isbn")} />,
    }),
    columnHelper.accessor("author", {
      enableHiding: false,
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("books:booktable.columns.author")}
        />
      ),
      cell: ({ row }) => {
        const authors = row.getValue<string[] | undefined>("author") ?? [];
        return <TruncatedText text={authors.join(", ")} />;
      },
    }),
    columnHelper.accessor("title", {
      enableHiding: false,
      size: 240,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("books:booktable.columns.title")}
        />
      ),
      cell: ({ row }) => {
        const product = row.original;
        return (
          <Link
            to="/books/$productId"
            params={{ productId: product.id }}
            state={options.linkState}
            onClick={(e) => e.stopPropagation()}
            className="block w-fit max-w-full rounded-sm text-secondary-700 transition-colors hover:text-primary-500 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
          >
            <TruncatedText text={product.title} />
          </Link>
        );
      },
    }),
    columnHelper.accessor("productGroupDescription", {
      enableHiding: false,
      size: 140,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("books:booktable.columns.productForm")}
        />
      ),
      cell: ({ row }) => {
        const value =
          row.getValue<string | undefined>("productGroupDescription") || "-";
        return (
          <Text as="span" variant="body">
            {value}
          </Text>
        );
      },
    }),
    columnHelper.display({
      id: MATCH_TYPE_COLUMN_ID,
      size: 120,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("books:booktable.columns.matchType")}
        />
      ),
      cell: ({ row }) => (
        <MatchTypeBadges
          sources={deriveMatchSources(row.original.matched_queries)}
        />
      ),
    }),
  ]);
